import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScalesIcon } from '@phosphor-icons/react';
import { Button, Dialog, EmptyState } from '@origo/origo-ui';
import PlanDetailHeader from '../../components/PlanDetailHeader';
import DispensationGroupHeader from './components/DispensationGroupHeader';
import type { DispensationGroup } from '../../types';
import styles from './DispensationsPage.module.css';

interface DispensationsPageProps {
    groups: DispensationGroup[];
    openGroups: Set<DispensationGroup['key']>;
    onToggleGroup: (key: DispensationGroup['key']) => void;
}

const DispensationsPage: React.FC<DispensationsPageProps> = ({ groups, openGroups, onToggleGroup }) => {
    const { t } = useTranslation();
    const [stubOpen, setStubOpen] = useState(false);

    const isEmpty = groups.length === 0 || groups.every(g => g.items.length === 0);

    return (
        <article className={styles.page}>
            <PlanDetailHeader title={t('dispensations.title')} subtitle={t('dispensations.subtitle')} />
            <div className={styles.body}>
                {isEmpty ? (
                    <EmptyState
                        icon={<ScalesIcon size={32} weight="duotone" />}
                        title={t('dispensations.empty.title')}
                        description={t('dispensations.empty.description')}
                        action={
                            <Button variant="primary" size="lg" onClick={() => setStubOpen(true)}>
                                {t('dispensations.empty.action')}
                            </Button>
                        }
                    />
                ) : (
                    groups.map(group => (
                        <DispensationGroupHeader
                            key={group.key}
                            label={t(`dispensations.groups.${group.key}`)}
                            count={group.count}
                            open={openGroups.has(group.key)}
                            onToggle={() => onToggleGroup(group.key)}
                        >
                            {group.items.length === 0 ? (
                                <p className={styles.empty}>{t('dispensations.emptyGroup')}</p>
                            ) : (
                                <ul className={styles.list}>
                                    {group.items.map(item => (
                                        <li key={item.id} className={styles.item}>
                                            <span className={styles.itemTitle}>{item.tittel}</span>
                                            <span className={styles.itemMeta}>
                                                {item.saksnummer}
                                                {item.dato ? ` · ${item.dato}` : ''}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </DispensationGroupHeader>
                    ))
                )}
            </div>
            <Dialog
                open={stubOpen}
                onClose={() => setStubOpen(false)}
                title={t('dispensations.actionStub.title')}
                footer={
                    <Button variant="primary" onClick={() => setStubOpen(false)}>
                        {t('dispensations.actionStub.close')}
                    </Button>
                }
            >
                <p>{t('dispensations.actionStub.body')}</p>
            </Dialog>
        </article>
    );
};

export default DispensationsPage;
